import { usePrevious } from '@sequencehq/utils'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useSelectCoverImageSrc } from 'Cube/domain/subdomain/coverImage'
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'

type CoverImageEditorContextData = {
  imageSrc?: string
  setImageSrc: (val?: string) => void
  file?: File
  prevImageSrc?: string
  setFile: (val?: File) => void
  isDisabled?: boolean
}

export const CoverImageEditorContext =
  createContext<CoverImageEditorContextData>({
    setImageSrc: _ => {
      _
    },
    setFile: _ => {
      _
    }
  })

export const useCoverImageEditorContext = () => {
  return useContext(CoverImageEditorContext)
}

export const CoverImageEditorProvider = ({ children }: PropsWithChildren) => {
  const cubeContext = useCubeContext()
  const assetSrc = useSelectCoverImageSrc()

  const [file, setFile] = useState<File | undefined>(undefined)

  // We store this so there's no content jump when the image finishes uploading,
  // and so we can transition out without the image disappearing immediately
  const [imageSrc, setImageSrc] = useState<string | undefined>(assetSrc)

  const prevImageSrc = usePrevious(imageSrc)

  // Track every blob URL we've ever set so we can revoke them all on unmount.
  // We don't revoke immediately on change because prevImageSrc may be restored
  // on upload failure, and the editor reads from prevImageSrc to roll back.
  const blobUrlsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (imageSrc?.startsWith('blob:')) {
      blobUrlsRef.current.add(imageSrc)
    }
  }, [imageSrc])
  useEffect(() => {
    const blobUrls = blobUrlsRef.current
    return () => {
      blobUrls.forEach(url => URL.revokeObjectURL(url))
      blobUrls.clear()
    }
  }, [])

  return (
    <CoverImageEditorContext.Provider
      value={{
        file,
        setFile,
        imageSrc,
        setImageSrc,
        prevImageSrc,
        isDisabled:
          !cubeContext.queries.availableFeatures.quote.edit.available.enabled
      }}
    >
      {children}
    </CoverImageEditorContext.Provider>
  )
}
